import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import NowPlaying from './NowPlaying';
import QuickActions from './QuickActions';
import Timeline from './Timeline';
import ListenersPanel from './ListenersPanel';
import SiteActivityPanel from './SiteActivityPanel';
import useRadioControl from '../../hooks/useRadioControl';

const gbFmt = (b) => (b ? (b / 1024 ** 3).toFixed(1) : '0');

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const canControl = user?.role === 'admin' || user?.role === 'operator';
  const { status, toggle } = useRadioControl();

  const geo = stats?.listeners?.geo || null;
  const disk = stats?.disk || null;
  const diskPct = disk ? Math.round(disk.used_pct ?? 0) : 0;

  useEffect(() => {
    loadDashboard();
    const interval = setInterval(loadDashboard, 5000);
    return () => clearInterval(interval);
  }, []);

  async function loadDashboard() {
    try {
      const res = await api.get('/api/dashboard');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load dashboard', err);
    } finally {
      setLoading(false);
    }
  }

  // Apagar corta el stream para todos: pedir confirmación.
  const handleToggleRadio = async () => {
    if (status === 'ONLINE' && !window.confirm('⚠️ ¿Detener la radio?\n\nEl streaming se cortará para todos los oyentes hasta que la vuelvas a encender. ¿Continuar?')) {
      return;
    }
    await toggle();
  };

  if (loading) return <div className="p-6">Cargando panel...</div>;

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-lg font-bold text-dark-100">Panel de control</h1>

      {/* Estado general: línea compacta (36px). Disco con detalle, es el importante */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Estado de la radio — mismo formato que el ONLINE del header */}
        <button
          type="button"
          onClick={canControl ? handleToggleRadio : undefined}
          title={canControl ? (status === 'ONLINE' ? 'Apagar la radio (pide confirmación)' : 'Encender la radio') : 'Solo operadores pueden apagar/encender'}
          className={`flex h-9 shrink-0 items-center gap-2 rounded-md border px-3 transition-[filter] ${
            status === 'ONLINE'
              ? 'border-success-500 bg-success-600 shadow-[0_0_10px_rgba(22,163,74,.5)]'
              : 'border-error-500 bg-error-600 shadow-[0_0_10px_rgba(220,38,38,.5)]'
          } ${canControl ? 'hover:brightness-110' : 'cursor-default'}`}
        >
          <span className="relative flex h-2 w-2">
            {status === 'ONLINE' && (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75"></span>
            )}
            <span className="relative inline-flex h-2 w-2 rounded-full bg-white"></span>
          </span>
          <span className="text-sm font-extrabold uppercase leading-none tracking-wider text-white">
            {status || '…'}
          </span>
          {canControl && (
            <span className="text-[9px] font-normal normal-case opacity-70">
              {status === 'ONLINE' ? 'apagar' : 'encender'}
            </span>
          )}
        </button>

        {/* Almacenamiento (biblioteca de medios) */}
        <span
          title={`Biblioteca de medios: ${gbFmt(stats?.storage?.total_bytes)} GB de audio`}
          className="flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-dark-700 bg-dark-800/60 px-2.5 text-[11px] text-dark-300"
        >
          💾 <b className="text-dark-100">{stats?.storage?.total_files ?? 0}</b>
          <span className="text-dark-500">archivos</span>
          <span className="tabular-nums text-dark-600">· {gbFmt(stats?.storage?.total_bytes)} GB</span>
        </span>

        {/* Disco del servidor — el importante: barra + % + detalle */}
        {disk && disk.total_bytes != null && (
          <div
            className="flex h-9 min-w-[300px] flex-1 items-center gap-2 rounded-md border border-dark-700 bg-dark-800/60 px-2.5"
            title={`Disco del servidor: ${gbFmt(disk.used_bytes)} GB usados de ${gbFmt(disk.total_bytes)} GB (${diskPct}%) · libre ${gbFmt(disk.free_bytes)} GB`}
          >
            <span className="shrink-0 text-[11px] font-bold text-dark-200">💽 Disco</span>
            <div className="h-2 min-w-[70px] flex-1 overflow-hidden rounded-full bg-dark-700">
              <div
                className={`h-full rounded-full transition-[width] ${
                  diskPct >= 90 ? 'bg-error-500' : diskPct >= 75 ? 'bg-warning-400' : 'bg-success-500'
                }`}
                style={{ width: `${Math.min(100, Math.max(0, diskPct))}%` }}
              />
            </div>
            <b className={`shrink-0 text-sm tabular-nums ${
              diskPct >= 90 ? 'text-error-400' : diskPct >= 75 ? 'text-warning-400' : 'text-success-400'
            }`}>
              {diskPct}%
            </b>
            <span className="shrink-0 text-[10px] tabular-nums text-dark-400">
              {gbFmt(disk.used_bytes)}/{gbFmt(disk.total_bytes)} GB
            </span>
            <span className="hidden shrink-0 text-[10px] tabular-nums text-dark-500 md:inline">
              libre {gbFmt(disk.free_bytes)} GB
            </span>
            {diskPct >= 85 && (
              <span className="shrink-0 text-[10px] font-medium text-error-400">⚠ limpiar</span>
            )}
          </div>
        )}
      </div>

      {/* Oyentes de la radio + actividad del sitio, lado a lado */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <ListenersPanel geo={geo} />
        <SiteActivityPanel />
      </div>

      {/* LIVE (al aire ahora) + línea de tiempo de la radio */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <NowPlaying data={stats?.now_playing} live={stats?.playback} schedule={stats?.schedule} />
        <Timeline stats={stats} />
      </div>

      <QuickActions />
    </div>
  );
}
